import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SortByAlphaIcon from "@mui/icons-material/SortByAlpha";
import { FullScreenDialog } from "../../../atoms/FullScreenDialog";
import { TopLevelPaper } from "../../../themes/StyledComponents";
import type { UserDto } from "../../../api-client";
import { compareUsersByName, sortUsersByColumnOrder } from "../../../helpers/calendarColumnOrder";
import { useSaveCalendarColumnOrder } from "../../../stores/User/user.mutations";

interface CalendarColumnOrderDialogProps {
  open: boolean;
  onClose: () => void;
  /** Location the order is being edited for; the order is per location. */
  locationId: string;
  locationName?: string;
  /** The location's users, as loaded by the admin table (carry columnSortRank). */
  users: UserDto[];
}

const displayName = (user: UserDto) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name || user.nickName || user.username || user.email || user.id || "";
};

/** Moves the item at `from` to `to`, returning a new array. */
const move = <T,>(items: T[], from: number, to: number): T[] => {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return items;
  }
  const next = items.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

/**
 * Full screen editor for the left-to-right order of the calendar's therapist
 * columns at one location.
 *
 * The order is location specific by design — a therapist working at several
 * locations can sit in a different position at each — so the dialog always
 * edits exactly one location, the one selected in the admin table.
 *
 * Deactivated users are hidden behind a toggle, defaulting to off — most of a
 * long-running location's users have left, and listing them buries the people
 * actually working there.
 *
 * Hidden is not the same as excluded. `ordered` always holds the location's
 * complete order and the save posts all of it; the toggle only decides what is
 * rendered. That matters because the save replaces the location's order
 * wholesale, so posting just the visible users would delete every deactivated
 * user's stored rank — and the calendar's own queries are `withDeleted()`, so
 * those users really can hold a column (a departed therapist still shows on the
 * historic dates they worked).
 */
const CalendarColumnOrderDialog: React.FC<CalendarColumnOrderDialogProps> = ({
  open,
  onClose,
  locationId,
  locationName,
  users,
}) => {
  const [ordered, setOrdered] = useState<UserDto[]>([]);
  const [showDeactivated, setShowDeactivated] = useState(false);
  // The ref is what the drag handlers read (always current); the state only
  // drives the dragged row's styling.
  const draggedId = useRef<string | null>(null);
  const [draggedUserId, setDraggedUserId] = useState<string | null>(null);
  const { mutateAsync: saveColumnOrder, isPending } = useSaveCalendarColumnOrder();

  // Only users with an id can be ranked; the admin table always delivers saved
  // records, but UserDto declares id optional because it doubles as a payload.
  const initial = useMemo(
    () => sortUsersByColumnOrder(users.filter((user) => !!user.id)),
    [users]
  );

  // Re-seed whenever the dialog opens (or the underlying list changes while
  // closed) so a cancelled edit never leaks into the next session. The toggle
  // resets with it: "show deactivated" is a way of looking at the list, not a
  // preference worth remembering.
  useEffect(() => {
    if (open) {
      setOrdered(initial);
      setShowDeactivated(false);
    }
  }, [open, initial]);

  // What the list renders. `ordered` stays complete underneath — see the note
  // on the component about why the save must still post the hidden users.
  const visible = useMemo(
    () => (showDeactivated ? ordered : ordered.filter((user) => !user.deletedAt)),
    [ordered, showDeactivated]
  );

  const deactivatedCount = useMemo(
    () => ordered.filter((user) => !!user.deletedAt).length,
    [ordered]
  );

  const isDirty = useMemo(
    () =>
      ordered.length !== initial.length ||
      ordered.some((user, index) => user.id !== initial[index]?.id),
    [ordered, initial]
  );

  const handleDragStart = (user: UserDto) => (event: React.DragEvent) => {
    draggedId.current = user.id ?? null;
    setDraggedUserId(user.id ?? null);
    event.dataTransfer.effectAllowed = "move";
    // Firefox only starts a drag when some data is set.
    event.dataTransfer.setData("text/plain", user.id ?? "");
  };

  const handleDragOver = (target: UserDto) => (event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (!draggedId.current) return;
    // Reorder as the pointer passes each row, so the list previews the result.
    // Both ends are located by id inside the updater rather than captured as
    // indices: dragover fires far faster than React re-renders, so a captured
    // index goes stale the moment the first move lands — and with deactivated
    // users hidden, a rendered index is not a position in `ordered` anyway.
    setOrdered((current) => {
      const from = current.findIndex((user) => user.id === draggedId.current);
      const to = current.findIndex((user) => user.id === target.id);
      return from === -1 || to === -1 ? current : move(current, from, to);
    });
  };

  const handleDragEnd = (event: React.DragEvent) => {
    event.preventDefault();
    draggedId.current = null;
    setDraggedUserId(null);
  };

  // The arrows step through the *visible* list. With deactivated users hidden a
  // rendered index is not a position in `ordered`, and stepping one place there
  // could swap a user with a hidden row — which reads as the button doing
  // nothing. So the neighbour is picked from what the user can see, and the
  // move is applied at that neighbour's position in the full list.
  const moveBy = useCallback(
    (visibleIndex: number, delta: number) => {
      const user = visible[visibleIndex];
      const neighbour = visible[visibleIndex + delta];
      if (!user || !neighbour) return;
      setOrdered((current) => {
        const from = current.findIndex((entry) => entry.id === user.id);
        const to = current.findIndex((entry) => entry.id === neighbour.id);
        return from === -1 || to === -1 ? current : move(current, from, to);
      });
    },
    [visible]
  );

  const handleSave = async () => {
    await saveColumnOrder({
      locationId,
      userIds: ordered.map((user) => user.id!).filter(Boolean),
    });
    onClose();
  };

  const title = (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
      <Typography variant="h6" component="span">
        Spaltenreihenfolge im Kalender
        {locationName ? ` – ${locationName}` : ""}
      </Typography>
    </Stack>
  );

  const actions = (
    <>
      <Button
        startIcon={<SortByAlphaIcon />}
        // Must be the app's own name ordering, not the displayed
        // "First Last" string: this button exists to restore the order an
        // unconfigured location shows, and that one is last name first.
        onClick={() => setOrdered((current) => current.slice().sort(compareUsersByName))}
        disabled={isPending}
      >
        Alphabetisch sortieren
      </Button>
      <Button onClick={() => setOrdered(initial)} disabled={!isDirty || isPending}>
        Zurücksetzen
      </Button>
      <Box sx={{ flexGrow: 1 }} />
      <Button onClick={onClose} disabled={isPending}>
        Abbrechen
      </Button>
      <Button variant="contained" onClick={handleSave} disabled={!isDirty || isPending}>
        Speichern
      </Button>
    </>
  );

  return (
    <FullScreenDialog open={open} onClose={onClose} titleComp={title} actions={actions}>
      <TopLevelPaper sx={{ width: "100%", height: "100%", overflowY: "auto" }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Ziehen Sie die Benutzer in die gewünschte Reihenfolge. Der oberste Eintrag ist im
          Kalender die linke Spalte. Die Reihenfolge gilt nur für diesen Standort und wirkt
          im Hauptkalender, in der Dauertermin-Ansicht und in der Arbeitszeiten-Ansicht.
        </Typography>

        {deactivatedCount > 0 && (
          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Switch
                checked={showDeactivated}
                onChange={(event) => setShowDeactivated(event.target.checked)}
              />
            }
            label={`Deaktivierte anzeigen (${deactivatedCount})`}
          />
        )}

        {visible.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {ordered.length === 0
              ? "Für diesen Standort sind keine Benutzer vorhanden."
              : "Für diesen Standort sind nur deaktivierte Benutzer vorhanden."}
          </Typography>
        ) : (
          <List dense disablePadding>
            {visible.map((user, index) => (
              <ListItem
                key={user.id}
                draggable
                onDragStart={handleDragStart(user)}
                onDragOver={handleDragOver(user)}
                onDragEnd={handleDragEnd}
                onDrop={handleDragEnd}
                sx={{
                  mb: 0.5,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                  cursor: "grab",
                  opacity: draggedUserId === user.id ? 0.5 : 1,
                  gap: 1,
                }}
              >
                <DragIndicatorIcon fontSize="small" color="action" />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ minWidth: 32, textAlign: "right" }}
                >
                  {index + 1}.
                </Typography>
                <Typography variant="body1" sx={{ flexGrow: 1 }}>
                  {displayName(user)}
                  {user.nickName ? ` (${user.nickName})` : ""}
                </Typography>
                {user.deletedAt && <Chip size="small" label="deaktiviert" variant="outlined" />}
                <Tooltip title="Nach links">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`${displayName(user)} nach links`}
                      onClick={() => moveBy(index, -1)}
                      disabled={index === 0}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Nach rechts">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`${displayName(user)} nach rechts`}
                      onClick={() => moveBy(index, 1)}
                      disabled={index === visible.length - 1}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        )}
      </TopLevelPaper>
    </FullScreenDialog>
  );
};

export default CalendarColumnOrderDialog;
